import { Controller } from "stimulus";
import { onTransitionEnd } from "../assets/utils";

const DIRECTION_LEFT = 'LEFT';
const DIRECTION_RIGHT = 'RIGHT';
const CLASSES = {
    HIDDEN: 'hidden',
    CONTROL_ACTIVE: ['fas', 'fa-circle'],
    CONTROL_INACTIVE: ['fal', 'fa-circle'],
};

const TRANSFORM = {
    LEFT: 'translate(-100%, 0)',
    RIGHT: 'translate(100%, 0)',
    ACTIVE: 'translate(0, 0)',
};

const TOUCHMOVE_THRESHOLD = 0.1;
const TRANSITION_DURATION = "0.3s";


export default class extends Controller {
    static targets = ['next', 'prev', 'slide', 'control', 'canvas'];

    initialize() {
        this.active = -1;
        this.touchEnabled = true;
        this.next = this.next.bind(this);
        this.prev = this.prev.bind(this);
        this.goto = this.goto.bind(this);
        this.onTouchstart = this.onTouchstart.bind(this);
        this.onTouchmove = this.onTouchmove.bind(this);
        this.onTouchend = this.onTouchend.bind(this);
        this.slideTargets.forEach(slide => {
            slide.style.transitionProperty = "transform";
            slide.style.transitionTimingFunction = "ease-in-out";
            slide.style.transitionDuration = "";
            slide.style.transform = TRANSFORM.LEFT;
            // slide.style.transitionDuration = TRANSITION_DURATION;
        });
        this.element.addEventListener('modal:opened', (event) => {
            if (event.detail && event.detail.data && 'slideIndex' in event.detail.data) {
                this.goto(event.detail.data.slideIndex);
            }
        });
    }

    connect() {
        this.canvasTarget.addEventListener('touchstart', this.onTouchstart);
        this.nextTarget.addEventListener('click', this.next);
        this.prevTarget.addEventListener('click', this.prev);
        this.controlTargets.forEach(elem => elem.addEventListener('click', this.goto));
        this.goto(this.data.get('active') || 0);
    }

    disconnect() {
        this.canvasTarget.removeEventListener('touchstart', this.onTouchstart);
        this.nextTarget.removeEventListener('click', this.next);
        this.prevTarget.removeEventListener('click', this.prev);
        this.controlTargets.forEach(elem => elem.removeEventListener('click', this.goto));
    }

    next() {
        let index = this.active + 1;
        if (index > this.slideTargets.length - 1) {
            index = 0;
        }
        this.goto(index);
    }

    prev() {
        let index = this.active - 1;
        if (index < 0) {
            index = this.slideTargets.length - 1;
        }
        this.goto(index);
    }

    goto(index) {
        if (index instanceof Event) {
            index = parseInt(index.target.dataset.index) || 0;
        }
        const current = this.active;
        if (index === current) {
            return;
        }
        const next = parseInt(index) || 0;
        const right = current < index;

        if (this.slideTargets[current]) {
            this.slide_out(this.slideTargets[current], right ? DIRECTION_LEFT : DIRECTION_RIGHT);
        }
        if (this.slideTargets[next]) {
            if (this.active === -1) {
                this.slide_in(this.slideTargets[next], 'ACTIVE');
            } else {
                this.slide_in(this.slideTargets[next], right ? DIRECTION_RIGHT : DIRECTION_LEFT);
            }
        }

        this.controlTargets.forEach((control, index) => {
            if (index == next) {
                control.classList.remove(...CLASSES.CONTROL_INACTIVE);
                control.classList.add(...CLASSES.CONTROL_ACTIVE);
            } else {
                control.classList.remove(...CLASSES.CONTROL_ACTIVE);
                control.classList.add(...CLASSES.CONTROL_INACTIVE);
            }
        });

        this.active = next;
        // Wenn ein Panorama geladen wurde können wir nicht gleichzeitig die Slides per touch bewegen
        this.touchEnabled = (this.slideTargets[this.active].querySelector('[data-controller="panorama"]') === null);
        this.prefetch(this.active);
    }

    slide_in(slide, direction) {
        slide.classList.remove(CLASSES.HIDDEN);
        slide.style.transitionDuration = "";
        if (slide.style.transform === TRANSFORM.ACTIVE) {
            return;
        }
        slide.style.transform = TRANSFORM[direction];
        setTimeout((slide) => {
            slide.style.transitionDuration = TRANSITION_DURATION;
            slide.style.transform = TRANSFORM.ACTIVE;
        }, 0, slide);
    }

    slide_out(slide, direction) {
        onTransitionEnd(slide).then(() => {
            slide.classList.add(CLASSES.HIDDEN);
            slide.style.transitionDuration = ""
        });
        slide.style.transitionDuration = TRANSITION_DURATION;
        slide.style.transform = TRANSFORM[direction];
    }

    /**
     * Bilder mit loading=lazy werden in Containern mit display: none nicht geladen.
     * Daher wird zum prefetchen hier nur display: none des Slides entfernt.
     */
    prefetch(index) {
        setTimeout(() => {
            [this.slideTargets[index - 1], this.slideTargets[index + 1]]
                .filter(slide => slide)
                .forEach(slide => slide.classList.remove('hidden'))
        }, 100);
    }

    get last_index() {
        return this.slideTargets.length - 1
    }

    onTouchstart(event) {
        if (this.touchEnabled === false) {
            return;
        }
        this.touch_screenX = event.touches[0].screenX;
        this.slideTargets[this.active].style.transitionDuration = "0s";
        this.canvasTarget.addEventListener("touchmove", this.onTouchmove);
        this.canvasTarget.addEventListener("touchend", this.onTouchend);

        if (this.active > 0) {
            this.slideTargets[this.active - 1].style.transform = TRANSFORM.LEFT;
            this.slideTargets[this.active - 1].style.transitionDuration = "0s";
            this.slideTargets[this.active - 1].classList.remove(CLASSES.HIDDEN);
        }
        if (this.active < this.last_index) {
            this.slideTargets[this.active + 1].style.transform = TRANSFORM.RIGHT;
            this.slideTargets[this.active + 1].style.transitionDuration = "0s";
            this.slideTargets[this.active + 1].classList.remove(CLASSES.HIDDEN);
        }

    }

    onTouchmove(event) {
        this.touch_delta = 0.7 * (event.touches[0].screenX - this.touch_screenX);
        this.slideTargets[this.active].style.transform = `translate(${this.touch_delta}px, 0)`;
        if (this.active > 0) {
            this.slideTargets[this.active - 1].style.transform = TRANSFORM.LEFT + ` translate(${this.touch_delta}px, 0)`;
        }
        if (this.active < this.last_index) {
            this.slideTargets[this.active + 1].style.transform = TRANSFORM.RIGHT + ` translate(${this.touch_delta}px, 0)`;
        }
        event.stopPropagation();
    }

    onTouchend(event) {
        const relative = this.touch_delta / this.element.offsetWidth;
        this.canvasTarget.removeEventListener("touchmove", this.onTouchmove);

        const active = this.slideTargets[this.active];
        const left = (this.active > 0) ? this.slideTargets[this.active - 1] : null;
        const right = (this.active < this.last_index) ? this.slideTargets[this.active + 1] : null;

        active.style.transitionDuration = TRANSITION_DURATION;
        left && (left.style.transitionDuration = TRANSITION_DURATION);
        right && (right.style.transitionDuration = TRANSITION_DURATION);

        setTimeout(() => {
            if (relative < -TOUCHMOVE_THRESHOLD && this.active < this.last_index) {
                this.next();
            } else if (relative > TOUCHMOVE_THRESHOLD && this.active > 0) {
                this.prev();
            } else {
                active.style.transform = TRANSFORM.ACTIVE;
                left && (left.style.transform = TRANSFORM.LEFT);
                right && (right.style.transform = TRANSFORM.RIGHT);
            }
        });
    }
}